<template>
  <div class="for-pc-page">
    <HeadNav selectedLanguage="en" page="for-pc" />

    <main>
      <section class="hero">
        <div class="hero-orb" aria-hidden="true" />
        <div class="container hero-copy">
          <p class="eyebrow">Windows VPN</p>
          <h1>Secure VPN for PC: Download & Use VPN on Windows</h1>
          <p class="lead">Secure VPN for PC helps Windows users protect their online privacy, secure internet connections, and browse the web with more control. Whether you're using public Wi-Fi, accessing websites while traveling, or simply looking for a safer browsing experience, Secure VPN provides an easy way to connect through a protected VPN network.</p>
          <router-link class="btn btn-primary" to="/download/">Get Secure VPN for PC</router-link>
        </div>
      </section>

      <section class="section">
        <div class="container prose">
          <h2>What Is Secure VPN for PC?</h2>
          <p>Secure VPN for PC is a VPN application designed for Windows computers that helps protect your online connection by routing your internet traffic through a VPN server.</p>
          <p>When you connect to a VPN server, your original IP address is replaced with the VPN server's IP address. This adds an extra layer of privacy while browsing websites, using online services, or connecting to public networks.</p>
          <p>A VPN for PC can be useful for:</p>
          <ul class="check-list">
            <li v-for="item in useCases" :key="item"><span class="dot" aria-hidden="true" /><span>{{ item }}</span></li>
          </ul>
          <p>Instead of connecting directly to the internet, your device connects through a secure VPN tunnel before reaching online services.</p>
          <figure class="inline-media">
            <img :src="previewImage" alt="Secure VPN for PC app preview">
          </figure>
        </div>
      </section>

      <section class="section section-tint">
        <div class="container">
          <div class="section-head">
            <h2>Why Use Secure VPN on a PC?</h2>
            <p>Many users spend hours online every day for work, entertainment, communication, and research. A VPN can help provide a safer browsing environment, especially when connecting through networks that you do not fully trust.</p>
          </div>
          <div class="card-grid">
            <article v-for="item in whyItems" :key="item.title" class="info-card">
              <h3>{{ item.title }}</h3>
              <p>{{ item.text }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="container">
          <div class="section-head">
            <h2>Secure VPN for PC Features</h2>
          </div>
          <div class="feature-grid">
            <article v-for="feature in features" :key="feature.title" class="info-card">
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.text }}</p>
              <ol v-if="feature.steps">
                <li v-for="step in feature.steps" :key="step">{{ step }}</li>
              </ol>
            </article>
          </div>
        </div>
      </section>

      <section class="section section-tint">
        <div class="container">
          <div class="section-head">
            <h2>How to Download Secure VPN for PC</h2>
            <p>Before installing Secure VPN on your computer, make sure your Windows device meets the required system specifications.</p>
            <p>Follow these general steps:</p>
          </div>
          <div class="steps">
            <article v-for="(step, index) in downloadSteps" :key="step.title" class="step-card">
              <span>{{ index + 1 }}</span>
              <div>
                <h3>{{ step.title }}</h3>
                <p>{{ step.text }}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="container">
          <div class="section-head">
            <h2>How to Install Secure VPN on Windows</h2>
            <p>Installing Secure VPN is similar to installing most Windows applications.</p>
          </div>
          <div class="install-panel">
            <ol class="install-list">
              <li v-for="(item, index) in installSteps" :key="item">
                <span class="install-num">{{ index + 1 }}</span>
                <span class="install-text">{{ item }}</span>
              </li>
            </ol>
          </div>
          <p class="note">After installation, you can adjust settings based on your browsing needs.</p>
        </div>
      </section>

      <section class="section section-tint">
        <div class="container">
          <div class="section-head">
            <h2>Secure VPN for PC vs Mobile Version</h2>
            <p>Secure VPN may be available on different platforms, but PC and mobile versions provide different experiences.</p>
          </div>
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th v-for="header in compareHeaders" :key="header">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in compareRows" :key="row[0]">
                  <td v-for="(cell, idx) in row" :key="idx">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="note">The PC version is better suited for users who spend more time browsing, working, or streaming on a computer.</p>
        </div>
      </section>

      <section class="section">
        <div class="container prose">
          <h2>Is Secure VPN Safe for PC?</h2>
          <p>A VPN can improve online privacy and security, but users should always understand how a VPN service handles data.</p>
          <p>Before using any VPN application, consider:</p>
          <ul class="check-list">
            <li v-for="item in safetyChecks" :key="item"><span class="dot" aria-hidden="true" /><span>{{ item }}</span></li>
          </ul>
          <p>A VPN helps protect your connection, but it does not make you completely anonymous online. Users should still follow safe browsing practices, avoid suspicious websites, and keep their devices updated.</p>
        </div>
      </section>

      <section class="section section-tint">
        <div class="container">
          <div class="section-head">
            <h2>Common Secure VPN PC Problems and Fixes</h2>
            <p>Even reliable VPN applications may occasionally experience connection or installation issues.</p>
          </div>
          <div class="card-grid">
            <article v-for="item in problems" :key="item.title" class="info-card">
              <h3>{{ item.title }}</h3>
              <p>{{ item.intro }}</p>
              <ul>
                <li v-for="tip in item.tips" :key="tip">{{ tip }}</li>
              </ul>
              <p v-if="item.footer">{{ item.footer }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="container prose">
          <h2>How to Improve Secure VPN Performance on PC</h2>
          <p>To get a better VPN experience:</p>
          <div v-for="item in performanceTips" :key="item.title" class="tip-block">
            <h3>{{ item.title }}</h3>
            <p>{{ item.text }}</p>
          </div>
        </div>
      </section>

      <section class="section section-tint">
        <div class="container">
          <div class="section-head">
            <h2>Frequently Asked Questions</h2>
          </div>
          <div class="faq-list">
            <article v-for="item in faqs" :key="item.question" class="faq-item">
              <h3>{{ item.question }}</h3>
              <p>{{ item.answer }}</p>
            </article>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/vpn-theme.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
  name: 'ForPcPage',
  components: {
    HeadNav,
  },
  data() {
    return {
      previewImage: require('@/assets/vpn/for-pc-preview.png'),
      useCases: [
        'Protecting your connection on public Wi-Fi',
        'Improving online privacy',
        'Securing internet traffic',
        'Accessing websites while traveling',
        'Reducing exposure of your real IP address',
      ],
      whyItems: [
        {
          title: 'Protect Your Internet Connection',
          text: 'Public Wi-Fi networks in airports, hotels, cafes, and shared spaces can sometimes expose users to security risks. Secure VPN helps protect your connection by encrypting your internet traffic.',
        },
        {
          title: 'Improve Online Privacy',
          text: "When connected to a VPN server, websites and online services see the VPN server's IP address instead of your original IP address. This helps reduce direct exposure of your location and network information.",
        },
        {
          title: 'Browse More Safely While Traveling',
          text: 'Travelers often connect to unfamiliar networks or use different internet environments. A VPN can provide a more consistent and protected browsing experience.',
        },
        {
          title: 'Secure Remote Work Connections',
          text: 'For people working from home or accessing online services remotely, using a VPN can add another security layer when connecting through different networks.',
        },
      ],
      features: [
        {
          title: 'One-Click VPN Connection',
          text: 'Users can usually connect to a VPN server with just a few clicks without complicated network settings. This makes it suitable for beginners who want a straightforward VPN experience.',
        },
        {
          title: 'Secure Internet Browsing',
          text: 'A VPN helps protect your internet traffic while connected. Whether you are browsing websites, checking emails, or using online services, your connection is routed through a protected VPN server.',
        },
        {
          title: 'IP Address Protection',
          text: "When Secure VPN is active, your public IP address is replaced with the VPN server's IP address. This can help provide more privacy while browsing online.",
        },
        {
          title: 'Multiple Server Locations',
          text: 'Many VPN services provide different server locations. Choosing a suitable server can help improve connection quality depending on your location and network conditions.',
        },
        {
          title: 'Fast and Stable Connection',
          text: 'A good VPN should balance security and performance. Secure VPN focuses on providing a smooth connection experience while maintaining privacy protection.',
        },
        {
          title: 'Simple User Interface',
          text: 'The PC application is designed to be easy to use. Most users can:',
          steps: [
            'Open the VPN application.',
            'Select a server.',
            'Click connect.',
            'Start browsing securely.',
          ],
        },
      ],
      downloadSteps: [
        {
          title: 'Find the Windows Version',
          text: 'Download the PC version that is compatible with your Windows system. Make sure you use a trusted download source to avoid modified or unsafe installation files.',
        },
        {
          title: 'Download the Installer',
          text: 'Click the download button and save the setup file on your computer. The file is usually saved in your Downloads folder.',
        },
        {
          title: 'Run the Installation File',
          text: 'Open the installer and follow the instructions shown on your screen. You may need to allow Windows permission before installation begins.',
        },
        {
          title: 'Launch Secure VPN',
          text: 'After installation is complete, open the application from your desktop or Start menu. Log in if required, then choose a VPN server.',
        },
        {
          title: 'Connect to VPN',
          text: 'Click the connect button and wait until the VPN connection is established. Once connected, your internet traffic will go through the VPN server.',
        },
      ],
      installSteps: [
        'Download the Secure VPN setup file.',
        'Open the downloaded file.',
        'Accept the installation agreement.',
        'Select the installation location.',
        'Complete the installation process.',
        'Open Secure VPN.',
        'Connect to your preferred server.',
      ],
      compareHeaders: ['Feature', 'PC Version', 'Mobile Version'],
      compareRows: [
        ['Windows Support', 'Yes', 'No'],
        ['Android Support', 'No', 'Yes'],
        ['Larger Display', 'Yes', 'No'],
        ['Keyboard and Mouse Control', 'Yes', 'No'],
        ['Public Wi-Fi Protection', 'Yes', 'Yes'],
        ['Easy Connection', 'Yes', 'Yes'],
      ],
      safetyChecks: [
        'Privacy policy',
        'Data collection practices',
        'Available security features',
        'App permissions',
        'Company reputation',
      ],
      problems: [
        {
          title: 'Secure VPN Not Connecting on PC',
          intro: 'If Secure VPN cannot connect, try these solutions:',
          tips: [
            'Check your internet connection',
            'Restart the VPN application',
            'Change to another server',
            'Restart your computer',
            'Disable other network tools temporarily',
          ],
          footer: 'A weak network connection is one of the most common reasons for VPN connection failures.',
        },
        {
          title: 'Secure VPN Connection Is Slow',
          intro: 'VPN speed depends on several factors, including server distance, network quality, and device performance. Try:',
          tips: [
            'Selecting a closer VPN server',
            'Closing unnecessary applications',
            'Switching networks',
            'Restarting the VPN connection',
          ],
        },
        {
          title: 'Secure VPN Cannot Be Installed',
          intro: 'If installation fails:',
          tips: [
            'Check available storage space',
            'Update Windows',
            'Run the installer as administrator',
            'Download the installer again',
            'Remove older VPN versions',
          ],
        },
        {
          title: 'Secure VPN Keeps Disconnecting',
          intro: 'Frequent disconnections may happen because of:',
          tips: [
            'Unstable internet connection',
            'Battery-saving settings',
            'Network restrictions',
            'Outdated application files',
          ],
          footer: 'Restarting the app or changing servers may solve the issue.',
        },
      ],
      performanceTips: [
        {
          title: 'Choose a Nearby Server',
          text: 'Servers closer to your location usually provide better speed and lower latency.',
        },
        {
          title: 'Keep the App Updated',
          text: 'Updates often include performance improvements and bug fixes.',
        },
        {
          title: 'Use a Stable Internet Connection',
          text: 'A reliable Wi-Fi or wired connection can improve VPN stability.',
        },
        {
          title: 'Close Unnecessary Programs',
          text: 'Background applications can consume bandwidth and system resources.',
        },
      ],
      faqs: [
        {
          question: 'Does Secure VPN work on Windows PC?',
          answer: 'Yes, Secure VPN can be used on compatible Windows computers to help protect internet connections and improve browsing privacy.',
        },
        {
          question: 'Is Secure VPN free for PC?',
          answer: 'Some VPN services offer free access, while additional features or advanced servers may require a premium plan.',
        },
        {
          question: 'Can Secure VPN hide my IP address?',
          answer: "When connected to a VPN server, Secure VPN can replace your original IP address with the VPN server's IP address.",
        },
        {
          question: 'Can I use Secure VPN for gaming?',
          answer: 'A VPN may work with gaming services, but performance depends on server location, network speed, and latency.',
        },
        {
          question: 'Does Secure VPN protect public Wi-Fi?',
          answer: 'A VPN can add protection when using public Wi-Fi by encrypting your internet connection.',
        },
        {
          question: 'Is Secure VPN better on PC or mobile?',
          answer: 'The best option depends on your needs. The PC version is better for desktop browsing, work, and long sessions, while mobile versions are designed for smartphones and tablets.',
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
.for-pc-page {
  background:
    radial-gradient(ellipse 70% 40% at 80% 0%, rgba(91, 79, 232, 0.1), transparent 55%),
    #f5f4fb;
  color: var(--vpn-ink);
  font-family: var(--vpn-font);
}

.container {
  width: var(--vpn-container);
  margin: 0 auto;
}

.hero {
  position: relative;
  overflow: hidden;
  padding: 48px 0 40px;
  background:
    radial-gradient(ellipse 60% 80% at 85% 30%, rgba(91, 79, 232, 0.28), transparent 50%),
    linear-gradient(145deg, #12122e 0%, #1e1b4b 55%, #2a2a6a 100%);
  color: #fff;
}

.hero-orb {
  position: absolute;
  width: 360px;
  height: 360px;
  left: -80px;
  bottom: -120px;
  background: radial-gradient(circle, rgba(139, 124, 255, 0.22), transparent 70%);
  pointer-events: none;
}

.hero-copy {
  position: relative;
}

.eyebrow {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--vpn-teal-bright);
}

h1 {
  margin: 0 0 18px;
  font-size: clamp(30px, 3.6vw, 44px);
  line-height: 1.12;
  letter-spacing: -0.03em;
  font-weight: 800;
}

.lead {
  margin: 0 0 24px;
  font-size: 17px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.84);
}

.inline-media {
  margin: 20px 0 0;
  text-align: center;
}

.inline-media img {
  display: block;
  width: min(100%, 720px);
  margin: 0 auto;
  border-radius: 18px;
  box-shadow: var(--vpn-shadow-soft);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0 28px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 800;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, var(--vpn-teal-bright), var(--vpn-teal));
  color: #fff;
  box-shadow: 0 14px 30px rgba(91, 79, 232, 0.32);
}

.section {
  padding: 72px 0;
}

.section-tint {
  background: linear-gradient(180deg, rgba(91, 79, 232, 0.05), transparent);
}

.section-head {
  margin-bottom: 28px;
}

.section-head h2,
.prose h2 {
  margin: 0 0 12px;
  font-size: clamp(26px, 3.2vw, 36px);
  font-weight: 800;
  letter-spacing: -0.02em;
}

.prose h3 {
  margin: 18px 0 8px;
  font-size: 18px;
  font-weight: 800;
}

.tip-block:first-of-type h3 {
  margin-top: 4px;
}

.tip-block p {
  margin: 0;
}

.section-head p,
.prose p,
.note {
  margin: 0 0 14px;
  font-size: 16px;
  line-height: 1.8;
  color: var(--vpn-ink-soft);
}

.install-panel + .note {
  margin-top: 18px;
}

.card-grid,
.feature-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.info-card,
.step-card,
.faq-item {
  padding: 22px;
  border: 1px solid var(--vpn-line);
  border-radius: 18px;
  background: #fff;
  box-shadow: var(--vpn-shadow-soft);
}

.info-card h3,
.step-card h3,
.faq-item h3 {
  margin: 0 0 10px;
  font-size: 18px;
  font-weight: 800;
}

.info-card p,
.step-card p,
.faq-item p,
.info-card li {
  margin: 0;
  font-size: 15px;
  line-height: 1.7;
  color: var(--vpn-ink-soft);
}

.info-card ul,
.info-card ol {
  margin: 10px 0 0;
  padding-left: 18px;
}

.info-card li + li {
  margin-top: 6px;
}

.check-list {
  list-style: none !important;
  margin: 0 0 16px !important;
  padding: 0;
  display: grid;
  gap: 10px;
}

.check-list li {
  list-style: none !important;
  display: grid;
  grid-template-columns: 10px 1fr;
  gap: 14px;
  align-items: start;
  padding: 12px 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid var(--vpn-line);
}

.dot {
  width: 8px;
  height: 8px;
  margin-top: 9px;
  border-radius: 50%;
  background: var(--vpn-teal);
}

.steps {
  display: grid;
  gap: 14px;
}

.step-card {
  display: grid;
  grid-template-columns: 42px 1fr;
  gap: 16px;
  align-items: start;
}

.step-card span {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--vpn-teal), var(--vpn-teal-deep));
  color: #fff;
  font-weight: 800;
}

.install-panel {
  padding: 8px 20px;
  border: 1px solid var(--vpn-line);
  border-radius: 18px;
  background: #fff;
  box-shadow: var(--vpn-shadow-soft);
}

.install-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.install-list li {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 14px;
  align-items: center;
  margin: 0;
  padding: 14px 0;
  border-bottom: 1px solid rgba(15, 48, 74, 0.06);
}

.install-list li:last-child {
  border-bottom: 0;
}

.install-num {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--vpn-teal), var(--vpn-teal-deep));
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  flex-shrink: 0;
}

.install-text {
  font-size: 15px;
  line-height: 1.6;
  color: var(--vpn-ink-soft);
}

.table-wrap {
  max-width: 560px;
  overflow-x: auto;
  border: 1px solid var(--vpn-line);
  border-radius: 16px;
  background: #fff;
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

th,
td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid rgba(15, 48, 74, 0.06);
  font-size: 14px;
  line-height: 1.45;
  word-break: break-word;
}

th:nth-child(1),
td:nth-child(1) {
  width: 46%;
}

th:nth-child(2),
td:nth-child(2),
th:nth-child(3),
td:nth-child(3) {
  width: 27%;
  text-align: center;
}

th {
  background: linear-gradient(135deg, rgba(91, 79, 232, 0.1), rgba(59, 105, 255, 0.06));
  font-weight: 800;
}

tr:last-child td {
  border-bottom: 0;
}

.faq-list {
  display: grid;
  gap: 12px;
}

@media (max-width: 900px) {
  .card-grid,
  .feature-grid {
    grid-template-columns: 1fr;
  }

  .btn {
    width: 100%;
  }
}
</style>
